import { AgentDot } from '../AgentDot';
import { Avatar } from './ui/Avatar';
import { MaestroMark } from './MaestroMark';
import { initialsFor, useMe } from '../../lib/me';
import { useProfilePhoto } from '../../lib/use-profile-photo';
import { useAgentName } from '../../lib/use-agent-name';
import { creatorLabel, type FileCreator } from '../../lib/file-browser';
import { useTranslation } from '../../lib/i18n';
import styles from './FileCreatedBy.module.css';

/**
 * Who made a file: a mark plus a name. ONE component, because three surfaces show this — the
 * per-workspace files table, the cross-workspace files list, and the "Agents at work" files strip — and
 * they were each drawing their own version. The strip drew a bare coloured dot with no name at all, which
 * read as decoration rather than attribution.
 *
 * The mark depends on WHO the writer is, and that distinction is the point:
 *
 * - **The user** gets their own avatar — the same photo the sidebar's account card shows, through the same
 *   `Avatar` + `useAccountSummary` pair, so the person cannot appear as a photo in one place and a coloured
 *   dot in another. A generic hued dot for "You" was the bug: it made the file look like an agent's.
 * - **The Maestro** gets its own brand mark. The team lead is reserved OUT of the agent colour system on
 *   purpose (see `MaestroMark`, and common-lib's `agentColor.ts`), so giving it a hued `AgentDot` painted the
 *   team lead as if it were one of the team.
 * - **A sub-agent** (and every other actor) gets an `AgentDot`, hued off its id so the same writer keeps one
 *   colour everywhere.
 *
 * The NAME comes from `creatorLabel`, which resolves the four tiers — the writer's own name, the live
 * assistant name for the renameable Maestro, a localized label per actor kind, then the kind humanised.
 */
export function FileCreatedBy({ creator, size = 16 }: { creator: FileCreator; size?: number }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  // The narrow pair rather than `useAccountSummary`: that hook also wires navigation and BI for the sidebar's
  // account card, none of which a table cell wants. The PHOTO decision is still the shared one.
  const me = useMe();
  const { photoUrl, onPhotoError } = useProfilePhoto(me);
  const actor = creator.type.toUpperCase();
  const isUser = actor === 'USER';
  const isMaestro = actor === 'MAESTRO';
  const label = creatorLabel(creator, t, agent.short);

  return (
    <span className={styles.root}>
      {isUser ? (
        <Avatar initials={initialsFor(me)} photoUrl={photoUrl} onPhotoError={onPhotoError} size={size} />
      ) : isMaestro ? (
        <MaestroMark size={size} />
      ) : (
        <AgentDot id={creator.type} size={size} />
      )}
      <span className={styles.name}>{label}</span>
    </span>
  );
}
